import React from 'react';

import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
	ChartWrapper,
	TooltipWrapper,
	TooltipHeader,
	TooltipRowTitle,
	TooltipRowValue,
	TooltipRow,
	ColorWrapper,
	ColorSymbol,
} from './ColumnChart.styles';
import { colors } from '@alixpartners/ui-components';
import isEqual from 'lodash/isEqual';
import { LEGEND } from '../utils';
import { renderToString } from 'react-dom/server';

// After this threshold highcharts misbehaves and squeeze
// columns to the last one and does not match axis labels
const HIGHCHARTS_MAX_PLOT_WIDTH = 100005;
const MIN_SCROLL_ITEM_WIDTH = 50;

const getMinItemWidth = count => {
	const width = Math.min(
		MIN_SCROLL_ITEM_WIDTH,
		Math.floor(HIGHCHARTS_MAX_PLOT_WIDTH / count)
	);

	return width;
};

export class ColumnChart extends React.Component {
	state = {
		hoverIndex: null,
	};

	chart = null;

	componentDidUpdate(prevProps) {
		const { series, isLoading, isScrollable, categories } = this.props;

		if (!isEqual(series, prevProps.series)) {
			this.setAxisLimit();

			if (this.chart) {
				this.chart.redraw();
			}
		}

		if (!isEqual(categories, prevProps.categories) && this.chart) {
			// Redraw labels on x-axis (sometimes its not shown properly)
			this.chart.xAxis[0].update({}, false);
			this.chart.redraw();
		}

		if (isScrollable !== prevProps.isScrollable && this.chart) {
			this.chart.redraw();
		}

		if (isLoading !== prevProps.isLoading && this.chart) {
			if (!isLoading) {
				this.chart.hideLoading();
			} else {
				this.chart.showLoading();
			}
		}
	}

	changeHoverIndex = hoverIndex => {
		this.setState({ hoverIndex });
	};

	getPlotBands = () => {
		const { series, onClick } = this.props;

		if (!series[0] || !onClick) {
			return [];
		}

		return series[0].data.map((_, index) => this.getPlotBandByIndex(index));
	};

	getPlotBandByIndex = index => {
		const { selectedIndex, plotBandStep = 0.5, onClick } = this.props;
		const { hoverIndex } = this.state;
		const isSelected = index === selectedIndex;
		const isHover = index === hoverIndex;

		return {
			from: index - plotBandStep,
			to: index + plotBandStep,
			color: isSelected || isHover ? colors.greyBg : 'transparent',
			...(isSelected
				? {
						label: {
							useHTML: true,
							text: '',
							verticalAlign: 'bottom',
							y: 50,
						},
				  }
				: {}),
			events: {
				click: () => {
					if (onClick) {
						onClick(index);
					}
				},
				mouseout: () => {
					if (onClick) {
						this.changeHoverIndex(null);
					}
				},
			},
		};
	};

	getChartOptions = () => {
		const {
			width,
			onClick,
			categories,
			series,
			title,
			spendFormatter,
			showTooltip,
			isScrollable,
			onRedirect,
		} = this.props;

		return {
			chart: {
				type: 'column',
				marginTop: 41,
				width,
				height: 310,
				style: { fontFamily: 'Roboto' },
				spacingBottom: 60,
				animation: false,
				...(isScrollable
					? {
							scrollablePlotArea: {
								minWidth:
									categories.length * getMinItemWidth(categories.length),
								scrollPositionX: 0,
							},
					  }
					: {}),
				events: {
					redraw: () => {
						this.transformSelection();
					},
					click: e => {
						if (onClick && e.target && e.target.point) {
							onClick(e.target.point.index);
						}
					},
				},
			},
			title: {
				text: '',
			},
			exporting: { enabled: false },
			credits: {
				enabled: false,
			},
			loading: {
				hideDuration: 500,
				showDuration: 500,
				labelStyle: {
					fontWeight: 'normal',
					letterSpacing: '0.3px',
				},
			},
			xAxis: {
				...(isScrollable
					? {
							min: 0,
							max: categories.length - 1,
					  }
					: {}),
				categories,
				title: {
					enabled: false,
				},
				lineWidth: 0,
				labels: {
					autoRotation: false,
					overflow: 'justify',
					crop: false,
					style: {
						fontSize: '11px',
						fontWeight: '400',
						lineHeight: '13px',
						letterSpacing: 0,
						color: colors.grey8,
					},
					formatter: function() {
						return this.value === ''
							? '(blank)'
							: this.value === 0
								? 'null'
								: this.value;
					},
					reserveSpace: false,
				},
				offset: 10,
				gridLineWidth: 0,
				plotBands: this.getPlotBands(),
			},
			yAxis: {
				title: {
					align: 'high',
					text: title,
					textAlign: 'right',
					rotation: 0,
					y: -30,
					offset: 15,
					style: {
						fontSize: '11px',
						fontWeight: '400',
						lineHeight: '13px',
						letterSpacing: 0,
						color: colors.grey8,
					},
				},
				gridLineWidth: 0,
				lineColor: 'black',
				lineWidth: 1,
				labels: {
					style: {
						fontSize: '11px',
						fontWeight: '400',
						lineHeight: '13px',
						letterSpacing: 0,
						color: colors.grey8,
					},
					formatter: function() {
						return spendFormatter(this.value, true);
					},
				},
				plotLines: [
					{
						color: 'black',
						width: 1,
						value: 0,
					},
				],
			},
			legend: {
				enabled: false,
			},
			tooltip: {
				enabled: !!showTooltip,
				outside: true,
				useHTML: true,
				backgroundColor: colors.white,
				borderWidth: 0,
				borderRadius: 2,
				formatter: function() {
					const { point } = this;

					if (!point) {
						return false;
					}

					const name = point.y < 0 ? LEGEND.RED : LEGEND.GREEN;

					return renderToString(
						<TooltipWrapper>
							<TooltipHeader>{point.category}</TooltipHeader>
							<TooltipRow>
								<ColorWrapper>
									<ColorSymbol color={point.color} />
								</ColorWrapper>
								<TooltipRowTitle>{name}:</TooltipRowTitle>
								<TooltipRowValue>{spendFormatter(point.y)}</TooltipRowValue>
							</TooltipRow>
						</TooltipWrapper>
					);
				},
			},
			plotOptions: {
				column: {
					dataLabels: {
						enabled: true,
						crop: false,
						overflow: 'none',
						inside: false,
						style: {
							fontWeight: '500',
						},
						formatter: function() {
							return spendFormatter(this.y, true);
						},
					},
				},
				series: {
					turboThreshold: 1000000,
					point: {
						events: {
							click: e => {
								if (onClick) {
									onClick(e.point.index);
								}

								if (onRedirect) {
									onRedirect(e.point.category);
								}
							},
							mouseOver: e => {
								if (onClick) {
									this.changeHoverIndex(e.target.index);
								}
							},
							mouseOut: () => {
								if (onClick) {
									this.changeHoverIndex(null);
								}
							},
						},
					},
					animation: false,
					borderWidth: 0,
					pointPadding: 0,
					maxPointWidth: 48,
					states: {
						hover: {
							enabled: !!onClick || !!onRedirect,
						},
					},
				},
			},
			series,
		};
	};

	transformSelection = () => {
		if (
			this.chart &&
			this.chart.xAxis &&
			this.chart.xAxis[0] &&
			this.chart.xAxis[0].plotLinesAndBands
		) {
			this.chart.xAxis[0].plotLinesAndBands.forEach(plotBand => {
				const transform = plotBand.options.label
					? 'translate(0, -66) scale(1, 1.58)'
					: 'translate(0, -66) scale(1, 1.45)';

				plotBand.svgElem.attr({
					transform,
				});
			});
		}
	};

	setAxisLimit = () => {
		if (!this.chart) {
			return;
		}

		const { yAxis } = this.chart;
		const max = Math.max(yAxis[0].dataMax, 0);
		const min = Math.min(yAxis[0].dataMin, 0);

		yAxis[0].setExtremes(min, max);
	};

	onChartInit = chart => {
		this.chart = chart;
		this.setAxisLimit();
		this.transformSelection();
	};

	render() {
		const { onClick } = this.props;

		return (
			<ChartWrapper
				onMouseLeave={() => {
					if (onClick) {
						this.changeHoverIndex(null);
					}
				}}
			>
				<HighchartsReact
					highcharts={Highcharts}
					options={this.getChartOptions()}
					callback={this.onChartInit}
				/>
			</ChartWrapper>
		);
	}
}

export default ColumnChart;
